import type { Control, FieldErrors, UseFormRegister } from 'react-hook-form';

import { Checkbox, FieldError } from '@/shared/ui';

import type { FormFieldConfig, FormSubmitValues, FormWidgetConfig } from './form-widget.types';
import { ConfigurableFormField } from './ConfigurableFormField';
import { FormGrid, ProfileGuidance } from './FormPresentation';

type ConfigurableFormFieldsProps = {
  accountFieldNames: string[];
  config: FormWidgetConfig;
  control: Control<FormSubmitValues>;
  effectiveValues: FormSubmitValues;
  errors: FieldErrors<FormSubmitValues>;
  formFields: FormFieldConfig[];
  isLoggedInScenario: boolean;
  privacyLinkUrl?: string;
  profileUrl?: string;
  register: UseFormRegister<FormSubmitValues>;
  /** En formularios por pasos, el consentimiento se acepta al final. */
  showPrivacy?: boolean;
};

export function ConfigurableFormFields({
  accountFieldNames,
  config,
  control,
  effectiveValues,
  errors,
  formFields,
  isLoggedInScenario,
  privacyLinkUrl,
  profileUrl,
  register,
  showPrivacy = true,
}: ConfigurableFormFieldsProps) {
  return (
    <FormGrid>
      {formFields.map((field) => {
        const error = errors[field.name]?.message;
        const isAccountField = isLoggedInScenario && accountFieldNames.includes(field.name);

        return (
          <ConfigurableFormField
            key={field.name}
            config={config}
            control={control}
            effectiveValues={effectiveValues}
            error={typeof error === 'string' ? error : undefined}
            field={field}
            isAccountField={isAccountField}
            register={register}
          />
        );
      })}

      {config.privacy.enabled && showPrivacy && (
        <PrivacyField
          config={config}
          error={
            typeof errors.privacyAccepted?.message === 'string'
              ? errors.privacyAccepted.message
              : undefined
          }
          privacyLinkUrl={privacyLinkUrl}
          register={register}
        />
      )}

      {isLoggedInScenario && (
        <div className="@lg:col-span-2 border-adipa-border border-t pt-4">
          <ProfileGuidance profileUrl={profileUrl} />
        </div>
      )}
    </FormGrid>
  );
}

function PrivacyField({
  config,
  error,
  privacyLinkUrl,
  register,
}: {
  config: FormWidgetConfig;
  error?: string;
  privacyLinkUrl?: string;
  register: UseFormRegister<FormSubmitValues>;
}) {
  return (
    <div className="@lg:col-span-2 flex flex-col gap-1">
      <Checkbox
        id={`${config.formId}-privacyAccepted`}
        hasError={Boolean(error)}
        aria-describedby={`${config.formId}-privacyAccepted-error`}
        label={
          <>
            {config.privacy.label}{' '}
            {privacyLinkUrl ? (
              <a
                href={privacyLinkUrl}
                className="text-adipa-primary font-medium underline hover:no-underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {config.privacy.linkLabel}
              </a>
            ) : (
              <span>{config.privacy.linkLabel}</span>
            )}{' '}
            de ADIPA.
          </>
        }
        {...register('privacyAccepted')}
      />
      <FieldError id={`${config.formId}-privacyAccepted-error`} message={error} />
    </div>
  );
}
